import React, { useMemo } from 'react';
import { Modal } from '@/components/common/AppModal';
import { useGetTooltipsQuery } from '@/app/common/api/tooltipsApi';
import FalconCloseButton from '@/components/common/FalconCloseButton';
import { FiCheck as Check, FiInfo as Info } from 'react-icons/fi';
import {
  addonCatalog,
  comparisonConfig,
  planComparisonRows,
  sortedComparisonPriceRows,
  toolCatalog,
  ui,
} from '../constants';
import { usePricingTariffs } from '../context/PricingTariffsContext';
import { resolveAccentColor } from '../utils';
import HintBadge from './HintBadge';
import PriceValue from './PriceValue';
import { buildTooltipBySlugMap } from '../utils/tooltipContent';
import { getFeatureHint, getFeaturePlanValue } from './comparisonHelpers';
import type { PeriodDays } from '../types';

interface ToolsComparisonModalProps {
  show: boolean;
  onClose: () => void;
}

function ToolsComparisonModal({ show, onClose }: ToolsComparisonModalProps) {
  const { plans } = usePricingTariffs();
  const { data: tooltips = [] } = useGetTooltipsQuery();
  const tooltipBySlug = useMemo(
    () => buildTooltipBySlugMap(tooltips),
    [tooltips],
  );

  const planPriceRows = useMemo(
    () =>
      sortedComparisonPriceRows.map((row) => {
        const period = row.period as PeriodDays;
        const standardPrice = plans.base.prices[period];
        const professionalPrice = plans.research.prices[period];

        return {
          ...row,
          standard: standardPrice ? <PriceValue price={standardPrice} /> : null,
          professional: professionalPrice ? (
            <PriceValue price={professionalPrice} />
          ) : null,
        };
      }),
    [plans],
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      scrollable
      centered
      className="pricing-tools-modal"
      dialogClassName="pricing-tools-modal-dialog"
      contentClassName="pricing-tools-modal-content"
    >
      <Modal.Header className="pricing-tools-modal-header border-0 pb-0">
        <div className="flex-grow-1">
          <span className="eyebrow fs--2">{comparisonConfig.modal.eyebrow}</span>
          <Modal.Title as="h2" className="pricing-tools-modal-title">
            {comparisonConfig.modal.title}
          </Modal.Title>
          <p className="pricing-tools-modal-description text-secondary fs--2">
            {comparisonConfig.modal.description}
          </p>
        </div>
        <FalconCloseButton
          onClick={onClose}
          aria-label={comparisonConfig.modal.closeLabel}
          className="pricing-tools-modal-close"
        />
      </Modal.Header>

      <Modal.Body className="pricing-tools-modal-body pt-2 text-900">
        <div className="tools-table-wrap rounded-4">
          <table className="tools-table text-secondary fs--2">
            <thead>
              <tr>
                <th className="text-900">{comparisonConfig.modal.columns.label}</th>
                <th className="text-900">
                  <span className="comparison-plan-head">
                    <img src={plans.base.logo} alt="" />
                    <span>{plans.base.name}</span>
                  </span>
                </th>
                <th className="text-900">
                  <span className="comparison-plan-head">
                    <img src={plans.research.logo} alt="" />
                    <span>{plans.research.name}</span>
                  </span>
                </th>
                <th className="text-900">{comparisonConfig.modal.columns.note}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="tools-section-row">
                <td className="text-900 fs--2" colSpan={4}>{comparisonConfig.modal.sections.differences}</td>
              </tr>
              {planComparisonRows.map((row) => (
                <tr className="plan-difference-row" key={row.label}>
                  <td className="text-900">
                    <strong className="text-1000">{row.label}</strong>
                  </td>
                  <td>{row.standard}</td>
                  <td>{row.professional}</td>
                  <td>{row.note}</td>
                </tr>
              ))}
              <tr className="tools-section-row">
                <td className="text-900 fs--2" colSpan={4}>{comparisonConfig.modal.sections.tools}</td>
              </tr>
              {toolCatalog.map((tool) => {
                const ToolIcon = tool.icon;

                return (
                  <React.Fragment key={tool.id}>
                    <tr className="tool-main-row">
                      <td className="text-900">
                        <div className="tool-name-cell">
                          <span className="tool-inline-icon rounded-3">
                            <ToolIcon size={20} />
                          </span>
                          <div>
                            <strong>
                              {tool.name}
                              {tool.badge === ui.common.noLimit ? (
                                <span className="tool-unlimited-badge fs--2">
                                  {' '}
                                  ({tool.badge})
                                </span>
                              ) : null}
                            </strong>
                            <small className="text-secondary">{tool.description}</small>
                          </div>
                        </div>
                      </td>
                      <td aria-label={`${tool.name} در ${plans.base.name}`}></td>
                      <td aria-label={`${tool.name} در ${plans.research.name}`}></td>
                      <td></td>
                    </tr>
                    {tool.limits.map((limit) => (
                      <tr className="tool-limit-row" key={limit.label}>
                        <td className="text-900">
                          <span className="feature-name">{limit.label}</span>
                        </td>
                        <td>{limit.standard}</td>
                        <td>{limit.professional}</td>
                        <td>{limit.note}</td>
                      </tr>
                    ))}
                    {tool.features.map((feature) => {
                      const featureHint = getFeatureHint(
                        feature,
                        feature.slug ? tooltipBySlug[feature.slug] : undefined,
                      );

                      return (
                        <tr className="tool-feature-item-row" key={feature.id}>
                          <td className="text-900">
                            <span className="feature-name">
                              <span>{feature.name}</span>
                              <HintBadge hint={featureHint} />
                            </span>
                          </td>
                          <td>
                            {getFeaturePlanValue(feature, 'standard')}
                          </td>
                          <td>
                            {getFeaturePlanValue(feature, 'professional')}
                          </td>
                          <td>
                            <span
                              className={`feature-note text-secondary ${
                                feature.note === ui.common.noLimit
                                  ? 'feature-note-blue'
                                  : ''
                              }`}
                            >
                              {feature.note}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <tr className="tools-section-row price-section-row">
                <td className="text-900 fs--2" colSpan={4}>{comparisonConfig.modal.sections.prices}</td>
              </tr>
              {planPriceRows.map((row) => (
                <tr className="plan-price-row" key={row.label}>
                  <td className="text-900">
                    <strong className="text-1000">{row.label}</strong>
                  </td>
                  <td>{row.standard}</td>
                  <td>{row.professional}</td>
                  <td>{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="comparison-discount-note fs--2 rounded-3">
          <Info size={16} />
          <span>{comparisonConfig.modal.discountNote}</span>
        </div>

        <section className="addon-comparison-section">
          <div className="addon-section-heading">
            <span className="eyebrow fs--2">{comparisonConfig.modal.sections.addonsEyebrow}</span>
            <h3 className="text-1000">{comparisonConfig.modal.sections.addonsTitle}</h3>
          </div>
          <div className="addon-card-grid">
            {addonCatalog.map((addon) => {
              const Icon = addon.icon;
              const addonKey = 'key' in addon ? addon.key : addon.id;

              return (
                <article
                  className={`addon-info-card addon-info-card-${addonKey} rounded-4`}
                  key={addonKey}
                  style={{ '--addon-color': resolveAccentColor(addon.color) } as React.CSSProperties}
                >
                  <span className="addon-info-icon rounded-3">
                    <Icon size={23} />
                  </span>
                  <div>
                    <strong className="text-1000">{addon.name}</strong>
                    <p className="text-secondary">{addon.description}</p>
                    <small>{addon.price}</small>
                  </div>
                  <ul className="text-secondary">
                    {'features' in addon &&
                      addon.features.map((feature) => (
                        <li key={feature}>
                          <Check size={13} />
                          {feature}
                        </li>
                      ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>
      </Modal.Body>
    </Modal>
  );
}

export default ToolsComparisonModal;
